// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: staff choose who may merge pull requests (and start releases) from Agents: one list for
// every repository, and a list per repository where it needs other people (it replaces the first
// there). Settings → Connections → Agents → Manage, under the machines; staff only (the rights API
// answers staff alone, so the section shows once it loads). Nobody is on it until added.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import styles from '@app/features/konvo_agents/components/AgentPullCard.module.css';
import AgentPulls from '@app/features/konvo_agents/state/AgentPulls';
import {MemberAvatar, MemberPicker} from '@app/features/konvo_connections/components/AssistantConnectorEditor';
import editorStyles from '@app/features/konvo_connections/components/AssistantConnectorEditor.module.css';
import AssistantCapabilities from '@app/features/konvo_connections/state/AssistantCapabilities';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import Users from '@app/features/user/state/Users';
import {AGENT_PULL_REPOSITORY, type AgentMergeRights} from '@fluxer/schema/src/domains/konvo/Agents';
import {useLingui} from '@lingui/react/macro';
import {IconTrash, IconX} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const People = observer(
	({ids, onChange, label}: {ids: ReadonlyArray<string>; onChange: (ids: Array<string>) => void; label: string}) => {
		const {t} = useLingui();
		const members = AssistantCapabilities.members;
		return (
			<div className={styles.rightsPeople}>
				<div className={editorStyles.chips}>
					{ids.map((id) => {
						const member = members.find((m) => m.user_id === id);
						// The directory leaves out the person looking (staff); known users have names too.
						const name = member?.name ?? Users.getUser(id)?.displayName ?? id;
						return (
							<span key={id} className={editorStyles.memberChip} data-flx="konvo-agents.merge-rights.person">
								{member && <MemberAvatar member={member} size={20} />}
								{name}
								<button
									type="button"
									onClick={() => onChange(ids.filter((other) => other !== id))}
									aria-label={t({
										message: `Remove ${name}`,
										comment: 'Merge rights settings: removes a person from the list; the name is the person.',
									})}
								>
									<IconX size={12} stroke={2} />
								</button>
							</span>
						);
					})}
				</div>
				<MemberPicker members={members} exclude={ids} label={label} onPick={(id) => onChange([...ids, id])} />
			</div>
		);
	},
);

export const AgentMergeRightsSettings = observer(() => {
	const {t} = useLingui();
	const [repo, setRepo] = useState('');
	useEffect(() => {
		// Only staff get an answer; for everyone else the section stays hidden.
		void AgentPulls.loadRights()
			.then(() => AssistantCapabilities.refreshMembers())
			.catch(() => undefined);
	}, []);
	const rights = AgentPulls.rights;
	if (!rights) return null;
	const save = (next: AgentMergeRights) => {
		void AgentPulls.saveRights(next).catch(() =>
			ToastCommands.error(
				t({message: "Couldn't save who may merge.", comment: 'Merge rights settings: saving failed.'}),
			),
		);
	};
	const repoName = repo.trim().toLowerCase();
	const repoValid = AGENT_PULL_REPOSITORY.test(repoName) && !rights.repos[repoName];
	const addLabel = t({message: 'Add a person', comment: 'Merge rights settings: placeholder of the person picker.'});
	return (
		<SettingsSection
			id="agents-merge-rights"
			title={t({message: 'Merging from Konvo', comment: 'Settings → Assistant: heading of the merge rights section.'})}
			description={t({
				message:
					'Who may merge pull requests and start releases from Agents. Everyone else can ask them. Merges use their own GitHub sign-in, so GitHub still checks their access.',
				comment: 'Settings → Assistant: description of the merge rights section (staff only).',
			})}
		>
			<div className={styles.rights} data-flx="konvo-agents.merge-rights">
				<div className={styles.rightsBlock}>
					<div className={styles.rightsTitle}>
						{t({
							message: 'Every repository',
							comment: 'Merge rights settings: the list that applies to all repositories.',
						})}
					</div>
					<People ids={rights.everywhere} label={addLabel} onChange={(everywhere) => save({...rights, everywhere})} />
				</div>
				{Object.entries(rights.repos).map(([name, ids]) => (
					<div key={name} className={styles.rightsBlock} data-flx="konvo-agents.merge-rights.repo">
						<div className={styles.rightsTitle}>
							<code>{name}</code>
							<span className={styles.rightsNote}>
								{t({
									message: 'instead of the list above',
									comment: 'Merge rights settings: a repository list replaces the every-repository list.',
								})}
							</span>
							<button
								type="button"
								className={styles.noteAsk}
								onClick={() => {
									const repos = {...rights.repos};
									delete repos[name];
									save({...rights, repos});
								}}
								aria-label={t({
									message: `Remove ${name}`,
									comment: 'Merge rights settings: removes a repository list; the name is owner/name.',
								})}
							>
								<IconTrash size={15} stroke={1.8} />
							</button>
						</div>
						<People
							ids={ids}
							label={addLabel}
							onChange={(next) => save({...rights, repos: {...rights.repos, [name]: next}})}
						/>
					</div>
				))}
				<form
					className={styles.rightsAdd}
					onSubmit={(event) => {
						event.preventDefault();
						if (!repoValid) return;
						save({...rights, repos: {...rights.repos, [repoName]: []}});
						setRepo('');
					}}
				>
					<Input
						value={repo}
						onChange={(event) => setRepo(event.target.value)}
						placeholder={t({
							message: 'owner/repository, for a list of its own',
							comment: 'Merge rights settings: field to add a repository with its own list, e.g. Nuvio-Software/konvo.',
						})}
						aria-label={t({
							message: 'Repository with its own list',
							comment: 'Merge rights settings: label of the field to add a repository.',
						})}
						data-flx="konvo-agents.merge-rights.repo-input"
					/>
					<button type="submit" className={styles.secondary} disabled={!repoValid}>
						{t({message: 'Add', comment: 'Merge rights settings: adds the repository typed in the field.'})}
					</button>
				</form>
			</div>
		</SettingsSection>
	);
});
